import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIsLabViewer } from '../../hooks/useIsLabViewer';
import { Link } from 'react-router-dom';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Button from 'react-bootstrap/Button';
import Spinner from 'react-bootstrap/Spinner';
import { PlusLg, XLg } from 'react-bootstrap-icons';
import NavigateToUrlIcon from '../Generic/NavigateToUrlIcon';
import ProtocolModal from '../Protocol/ProtocolModal';
import {
  attachProtocolsToProduct,
  detachProtocolsFromProduct,
  getProtocols,
} from '../../clients/protocol_client';
import {
  getProtocolErrorMessage,
  isShopCatalogProduct,
  toProtocolOptions,
} from '../../utils/protocols';
import { showToast } from '../../config_and_helpers/helpers';
import { logError, logInfo } from '../../config_and_helpers/logger';
import {
  abortListSearchController,
  isRunnableListSearchQuery,
} from '../../utils/listSearchRequest';
import './ProductComponentStyle.css';

const LOG_CONTEXT = 'ProductProtocolsSection';

const SEARCH_DEBOUNCE_MS = 300;

/**
 * Lab protocols linked to a product, rendered as a frosted side callout on the
 * product detail modal (same pattern as the opening statistics section).
 *
 * Seeded from the read-only `protocols` array on `get_details`, then kept in
 * local state so the attach/detach responses (which return the product's fresh
 * stub list) can replace it without refetching the whole product.
 *
 * @param {Object} props
 * @param {Object} props.product - Product detail payload from get_details.
 * @param {string} props.token - Access token for the current user.
 * @param {boolean} [props.isSupplier] - Supplier accounts get 403 on protocols.
 */
const ProductProtocolsSection = ({ product, token, isSupplier = false }) => {
  const [protocols, setProtocols] = useState(
    Array.isArray(product?.protocols) ? product.protocols : []
  );
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [pendingProtocolId, setPendingProtocolId] = useState(null);
  const [isAttaching, setIsAttaching] = useState(false);

  const isMountedRef = useRef(true);
  const debounceTimeoutRef = useRef(null);
  const currentRequestRef = useRef(null);

  // Global supplier catalog (Shop) rows cannot be linked — the backend 400s.
  const isViewer = useIsLabViewer();
  const isShopCatalog = isShopCatalogProduct(product);
  const isReadOnly = isShopCatalog || isViewer;

  useEffect(() => {
    setProtocols(Array.isArray(product?.protocols) ? product.protocols : []);
  }, [product?.id, product?.protocols]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      abortListSearchController(currentRequestRef);
    };
  }, []);

  useEffect(() => {
    if (!product?.id) return;
    logInfo(LOG_CONTEXT, 'Protocols section rendered', {
      productId: product.id,
      catNum: product.cat_num,
      protocolCount: protocols.length,
      isReadOnly,
    });
  }, [product?.id, product?.cat_num, protocols.length, isReadOnly]);

  const linkedIds = useMemo(
    () => new Set(protocols.map(protocol => String(protocol.id))),
    [protocols]
  );

  const searchProtocols = useCallback(
    searchValue => {
      abortListSearchController(currentRequestRef);

      const controller = new AbortController();
      currentRequestRef.current = controller;
      setIsSearching(true);

      getProtocols(token, null, {
        searchInput: searchValue,
        signal: controller.signal,
      })
        .then(response => {
          if (currentRequestRef.current !== controller) return;
          currentRequestRef.current = null;

          if (response?.canceled) {
            setIsSearching(false);
            return;
          }
          if (!isMountedRef.current) return;

          if (response?.success) {
            // Already-linked protocols would be a no-op attach; hide them.
            setOptions(
              toProtocolOptions(response.results).filter(
                option => !linkedIds.has(String(option.value))
              )
            );
          } else {
            setOptions([]);
            logError(LOG_CONTEXT, 'protocol search failed', {
              searchValue,
              status: response?.status,
            });
          }
          setIsSearching(false);
        })
        .catch(error => {
          if (currentRequestRef.current !== controller) return;
          currentRequestRef.current = null;
          if (!isMountedRef.current) return;
          logError(LOG_CONTEXT, 'protocol search threw', error);
          setOptions([]);
          setIsSearching(false);
        });
    },
    [token, linkedIds]
  );

  const handleInputChange = (event, value, reason) => {
    if (reason === 'reset') return;

    setInputValue(value);

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    if (!isRunnableListSearchQuery(value)) {
      setIsSearching(false);
      return;
    }

    debounceTimeoutRef.current = setTimeout(() => {
      searchProtocols(value);
    }, SEARCH_DEBOUNCE_MS);
  };

  const openPicker = () => {
    setIsPickerOpen(true);
    // Seed the dropdown with the first page so it is useful before typing.
    searchProtocols('');
  };

  const closePicker = () => {
    setIsPickerOpen(false);
    setInputValue('');
    setOptions([]);
    abortListSearchController(currentRequestRef);
  };

  const handleAttach = async option => {
    if (!option?.value) return;

    setIsAttaching(true);
    const result = await attachProtocolsToProduct(token, product.id, [
      option.value,
    ]);
    if (!isMountedRef.current) return;
    setIsAttaching(false);

    if (result?.success) {
      setProtocols(result.protocols);
      closePicker();
      showToast('Protocol linked to product.', 'success', 'top-right', 3000);
      return;
    }

    showToast(
      getProtocolErrorMessage(result, 'Could not link the protocol.'),
      'error',
      'top-right',
      4000
    );
  };

  const handleDetach = async protocol => {
    setPendingProtocolId(protocol.id);
    const result = await detachProtocolsFromProduct(token, product.id, [
      protocol.id,
    ]);
    if (!isMountedRef.current) return;
    setPendingProtocolId(null);

    if (result?.success) {
      setProtocols(result.protocols);
      showToast('Protocol unlinked from product.', 'success', 'top-right', 3000);
      return;
    }

    showToast(
      getProtocolErrorMessage(result, 'Could not unlink the protocol.'),
      'error',
      'top-right',
      4000
    );
  };

  if (isSupplier) {
    return null;
  }

  return (
    <div className="open-procurement-callout product-protocols-callout">
      <h6 className="open-procurement-callout__title">Protocols</h6>

      {protocols.length === 0 ? (
        <p className="text-muted mb-2">No protocols linked to this product.</p>
      ) : (
        <ul className="product-protocols-list">
          {protocols.map(protocol => (
            <li key={protocol.id} className="product-protocols-list__item">
              <ProtocolModal
                protocolObj={protocol}
                onSuccessfulSubmit={updated => {
                  if (!updated) return;
                  setProtocols(prev =>
                    prev.map(entry =>
                      entry.id === protocol.id
                        ? {
                            id: updated.id,
                            name: updated.name,
                            url: updated.url,
                          }
                        : entry
                    )
                  );
                }}
                triggerContent={protocol.name}
                triggerVariant="link"
                triggerClassName="product-protocols-list__name p-0 text-start"
              />

              <div className="product-protocols-list__actions">
                {protocol.url && (
                  <a
                    href={protocol.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Open the external document for ${protocol.name}`}
                  >
                    <NavigateToUrlIcon iconSize="1rem" />
                  </a>
                )}
                {!isReadOnly && (
                  <Button
                    variant="link"
                    size="sm"
                    className="product-protocols-list__remove"
                    aria-label={`Unlink ${protocol.name} from this product`}
                    disabled={pendingProtocolId === protocol.id}
                    onClick={() => handleDetach(protocol)}
                  >
                    {pendingProtocolId === protocol.id ? (
                      <Spinner animation="border" size="sm" role="status" />
                    ) : (
                      <XLg size="0.8rem" />
                    )}
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {isReadOnly ? (
        <p className="text-muted small mb-0">
          {isShopCatalog
            ? "Supplier catalogue items cannot be linked to protocols. Link your lab's own product instead."
            : 'This account is read-only, so protocols cannot be linked or unlinked here.'}
        </p>
      ) : isPickerOpen ? (
        <div className="product-protocols-picker">
          <Autocomplete
            options={options}
            inputValue={inputValue}
            value={null}
            loading={isSearching}
            disabled={isAttaching}
            filterOptions={x => x}
            onInputChange={handleInputChange}
            onChange={(event, option) => handleAttach(option)}
            getOptionLabel={option => option?.label ?? ''}
            isOptionEqualToValue={(option, value) =>
              Boolean(option && value) &&
              String(option.value) === String(value.value)
            }
            noOptionsText="No matching protocols"
            renderOption={(props, option) => (
              <Box component="li" {...props} key={option.value}>
                {option.label}
              </Box>
            )}
            renderInput={params => (
              <TextField
                {...params}
                size="small"
                label="Link a protocol"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isSearching || isAttaching ? (
                        <CircularProgress color="inherit" size={16} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <Button
            variant="link"
            size="sm"
            className="p-0 mt-1"
            onClick={closePicker}
          >
            Cancel
          </Button>
        </div>
      ) : (
        <Button
          variant="outline-primary"
          size="sm"
          className="d-inline-flex align-items-center gap-1"
          onClick={openPicker}
        >
          <PlusLg size="0.8rem" /> Add protocol
        </Button>
      )}

      {product?.cat_num && (
        <div className="product-protocols-callout__footer">
          <Link
            to={`/protocols?cat_num=${encodeURIComponent(product.cat_num)}`}
          >
            All protocols for {product.cat_num}
          </Link>
        </div>
      )}
    </div>
  );
};

export default ProductProtocolsSection;
